import { action, computed, makeObservable, observable } from 'mobx';
import { computedFn } from 'mobx-utils';
import AutoReplyV2 from '../models/autoReplyV2';
import processIncomingData from '../processIncomingData';
import * as utils from '../../helpers/utils';
import {
  checkForConflicts,
  isDateAfterCurrentDate,
  parseRecurringStatus,
} from '../../helpers/dateHelpers';

class AutoReplyV2Store {
  constructor(rootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
  }

  @observable autoRepliesV2 = [];

  @computed get getCustomAwayMessageAutoReplies() {
    return this.autoRepliesV2
      .filter((f) => f.AutoReplyType?.TypeId === utils.AutoReplyTypeEnum.CustomAwayMessage.TypeId)
      .sort((a, b) => new Date(a.CreationDate) - new Date(b.CreationDate));
  }

  @computed get getScheduledAutoReply() {
    return this.autoRepliesV2
      .filter((f) => f.AutoReplyType?.TypeId === utils.AutoReplyTypeEnum.ScheduledAutoReply.TypeId)
      .sort((a) => (a.IsDefault ? -1 : 1))
      .sort((a, b) => {
        if (a.IsDefault || b.IsDefault) {
          return 0;
        }

        return !a.IsDefault && new Date(a.CreationDate) - new Date(b.CreationDate);
      });
  }

  @computed get getActiveTextCustomAwayMessageAutoReply() {
    return this.autoRepliesV2.find(
      (f) =>
        f.AutoReplyType?.TypeId === utils.AutoReplyTypeEnum.CustomAwayMessage.TypeId &&
        f.IsActiveForTextChat &&
        (!f.ExpirationTime || (f.ExpirationTime && isDateAfterCurrentDate(f.ExpirationTime)))
    );
  }

  @computed get getActiveWebCustomAwayMessageAutoReply() {
    return this.autoRepliesV2.find(
      (f) =>
        f.AutoReplyType?.TypeId === utils.AutoReplyTypeEnum.CustomAwayMessage.TypeId &&
        f.IsActiveForWebChat &&
        (!f.ExpirationTime || (f.ExpirationTime && isDateAfterCurrentDate(f.ExpirationTime)))
    );
  }

  @computed get getActiveTextScheduledAutoReply() {
    return this.autoRepliesV2.filter(
      (f) =>
        f.AutoReplyType?.TypeId === utils.AutoReplyTypeEnum.ScheduledAutoReply.TypeId &&
        f.IsActiveForTextChat
    );
  }

  @computed get getActiveWebScheduledAutoReply() {
    return this.autoRepliesV2.filter(
      (f) =>
        f.AutoReplyType?.TypeId === utils.AutoReplyTypeEnum.ScheduledAutoReply.TypeId &&
        f.IsActiveForWebChat
    );
  }

  @computed get getActiveAfterHoursTextScheduledAutoReply() {
    return this.autoRepliesV2.filter(
      (f) =>
        f.AutoReplyType?.TypeId === utils.AutoReplyTypeEnum.ScheduledAutoReply.TypeId &&
        f.IsActiveForTextChat &&
        f.IsDefault &&
        f.Title === 'Default After Hours'
    );
  }

  @computed get getActiveAfterHoursWebScheduledAutoReply() {
    return this.autoRepliesV2.filter(
      (f) =>
        f.AutoReplyType?.TypeId === utils.AutoReplyTypeEnum.ScheduledAutoReply.TypeId &&
        f.IsActiveForWebChat &&
        f.IsDefault &&
        f.Title === 'Default After Hours'
    );
  }

  computedGetConflictingScheduledAutoReplies = computedFn(
    function computedGetConflictingScheduledAutoReplies(selectedAutoReply, isActiveForMessageType) {
      return this.autoRepliesV2.filter((f) => {
        const isActiveForTextChat =
          f.AutoReplyType.TypeId === utils.AutoReplyTypeEnum.ScheduledAutoReply.TypeId &&
          isActiveForMessageType === utils.MessageTypeEnum.SMS.TypeId &&
          f.IsActiveForTextChat;
        const isActiveForWebChat =
          f.AutoReplyType.TypeId === utils.AutoReplyTypeEnum.ScheduledAutoReply.TypeId &&
          isActiveForMessageType === utils.MessageTypeEnum.DirectMessage.TypeId &&
          f.IsActiveForWebChat;

        if (
          f._id !== selectedAutoReply._id &&
          selectedAutoReply.RecurringStatus &&
          f.RecurringStatus &&
          (isActiveForTextChat || isActiveForWebChat)
        ) {
          const recurringStatusForSelected = parseRecurringStatus(
            selectedAutoReply.RecurringStatus
          );
          const recurringStatusForCurrent = parseRecurringStatus(f.RecurringStatus);

          return recurringStatusForSelected.some((selectedStatus) =>
            recurringStatusForCurrent.some((currentStatus) =>
              checkForConflicts(selectedStatus, currentStatus)
            )
          );
        }

        return false;
      });
    }
  );

  @action setDeleteAutoReply(data) {
    this.autoRepliesV2 = this.autoRepliesV2.filter((f) => f._id !== data._id);
  }

  @action UpsertAndGet(incObj) {
    return processIncomingData(incObj, this.rootStore, this.autoRepliesV2, AutoReplyV2);
  }
}

export default AutoReplyV2Store;
